import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import useSiteInfo, { telHref } from "@/hooks/useSiteInfo";
import { CallbackForm } from "@/components/CallbackForm";
import { CustomBlock, BlockFrame } from "@/components/home/CustomBlocks";
import { BLOCK_SCHEMAS, DEFAULT_LAYOUT, parseStats, safeImage } from "@/components/home/pageBlocks";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Wrench, 
  Car, 
  Shield, 
  Clock, 
  Award, 
  Users, 
  ArrowRight,
  CheckCircle,
  Star,
  Phone,
  Tag,
  ChevronLeft,
  ChevronRight,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const site = useSiteInfo();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroImageUrl, setHeroImageUrl] = useState("");
  const [aktionen, setAktionen] = useState([]);
  const [currentAktionIndex, setCurrentAktionIndex] = useState(0);
  const preview = new URLSearchParams(window.location.search).get("preview") === "1";
  const [layout, setLayout] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (preview && window.parent !== window) {
      const onMsg = (e) => {
        if (e.origin !== window.location.origin || !e.data) return;
        if (e.data.type === "at-editor-layout") setLayout(e.data.blocks);
        if (e.data.type === "at-editor-selected") {
          setSelectedId(e.data.id);
          const el = e.data.scroll && document.getElementById(`block-${e.data.id}`);
          if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 96, behavior: "smooth" });
        }
      };
      window.addEventListener("message", onMsg);
      window.parent.postMessage({ type: "at-editor-ready" }, window.location.origin);
      return () => window.removeEventListener("message", onMsg);
    }
    axios.get(`${API}/page-layout/home`)
      .then((r) => setLayout(Array.isArray(r.data.blocks) ? r.data.blocks : DEFAULT_LAYOUT))
      .catch(() => setLayout(DEFAULT_LAYOUT));
  }, [preview]);
  const [features, setFeatures] = useState({
    feature_fahrzeuge: true,
    feature_aktionen: true,
    feature_termine: true,
    feature_bewertungen: true,
    feature_kontakt: true,
    feature_reifenlager: true,
    feature_kundenportal: true
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch settings for hero image and features
        const settingsRes = await axios.get(`${API}/settings`);
        if (settingsRes.data.hero_image_url) {
          const url = settingsRes.data.hero_image_url;
          setHeroImageUrl(url.startsWith('http') ? url : `${BACKEND_URL}${url}`);
        }
        // Set feature toggles
        setFeatures({
          feature_fahrzeuge: settingsRes.data.feature_fahrzeuge ?? true,
          feature_aktionen: settingsRes.data.feature_aktionen ?? true,
          feature_termine: settingsRes.data.feature_termine ?? true,
          feature_bewertungen: settingsRes.data.feature_bewertungen ?? true,
          feature_kontakt: settingsRes.data.feature_kontakt ?? true,
          feature_reifenlager: settingsRes.data.feature_reifenlager ?? true,
          feature_kundenportal: settingsRes.data.feature_kundenportal ?? true
        });
        
        // Fetch vehicles (no auto-seeding)
        const response = await axios.get(`${API}/vehicles`);
        setVehicles(response.data.slice(0, 3));
        
        // Fetch active promotions
        const aktionenRes = await axios.get(`${API}/aktionen`);
        // Filter active and sort by priority (highest first)
        const activeAktionen = aktionenRes.data
          .filter(a => a.ist_aktiv)
          .sort((a, b) => (b.prioritaet || 0) - (a.prioritaet || 0));
        setAktionen(activeAktionen);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-rotate aktionen carousel
  useEffect(() => {
    if (aktionen.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentAktionIndex((prev) => (prev + 1) % aktionen.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [aktionen.length]);

  const nextAktion = () => {
    setCurrentAktionIndex((prev) => (prev + 1) % aktionen.length);
  };

  const prevAktion = () => {
    setCurrentAktionIndex((prev) => (prev - 1 + aktionen.length) % aktionen.length);
  };

  const formatDateDE = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    } catch {
      return dateString;
    }
  };

  const services = [
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Werkstatt-Service",
      description: "Professionelle Wartung und Reparaturen für alle Renault und Dacia Modelle.",
      features: ["Inspektion", "Ölwechsel", "Bremsenservice"]
    },
    {
      icon: <Car className="w-8 h-8" />,
      title: "Fahrzeugverkauf",
      description: "Große Auswahl an Neu- und Gebrauchtwagen zu fairen Preisen.",
      features: ["Neuwagen", "Gebrauchtwagen", "Finanzierung"]
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "TÜV & HU",
      description: "Hauptuntersuchung und alle Prüfungen direkt bei uns im Haus.",
      features: ["HU/AU", "Abgasuntersuchung", "Sicherheitscheck"]
    }
  ];

  const stats = [
    { value: "25+", label: "Jahre Erfahrung" },
    { value: "5000+", label: "Zufriedene Kunden" },
    { value: "100%", label: "Renault-Qualität" },
    { value: "24h", label: "Notfall-Service" }
  ];

  const testimonials = [
    {
      name: "Thomas M.",
      text: "Hervorragender Service! Mein Dacia wurde schnell und professionell repariert. Absolut empfehlenswert!",
      rating: 5
    },
    {
      name: "Sarah K.",
      text: "Faire Preise und kompetente Beratung beim Kauf meines neuen Renault Captur. Vielen Dank!",
      rating: 5
    },
    {
      name: "Michael B.",
      text: "Die Werkstatt meines Vertrauens. Schnelle Terminvergabe und top Qualität.",
      rating: 5
    }
  ];

  const builtins = {
    hero: (p) => (
<>
<section className="relative h-[600px] md:h-[700px] overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: (safeImage(p.image_url, BACKEND_URL) || heroImageUrl)
              ? `url('${safeImage(p.image_url, BACKEND_URL) || heroImageUrl}')`
              : "url('/img/hero-werkstatt.jpg')"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <Badge className="bg-[#EFDF00] text-black hover:bg-[#EFDF00] mb-6 font-bold">
              {p.badge ?? "Ihr Renault & Dacia Partner"}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 normal-case">
              {p.title ?? "Auto Thurm"}
            </h1>
            <p className="text-lg md:text-xl text-[#D9D9D6] mb-8 leading-relaxed">
              {p.subtitle ?? "Kompetente Beratung, erstklassiger Service und eine große Auswahl an Fahrzeugen. Willkommen bei Ihrem Renault und Dacia Spezialisten."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              {features.feature_fahrzeuge && (
              <Link to="/fahrzeuge">
                <Button 
                  className="bg-[#EFDF00] text-black hover:bg-[#D9C900] font-bold px-8 py-6 text-lg"
                  data-testid="hero-fahrzeuge-btn"
                >
                  {p.btn1_label ?? "Fahrzeuge entdecken"}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              )}
              {features.feature_termine && (
              <Link to="/termin">
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg bg-transparent"
                  data-testid="hero-termin-btn"
                >
                  {p.btn2_label ?? "Termin buchen"}
                </Button>
              </Link>
              )}
            </div>
          </div>
        </div>
      </section>
</>
    ),
    aktionen: (p) => (
features.feature_aktionen && aktionen.length > 0 && (
        <section className="relative bg-gradient-to-r from-[#EFDF00] to-yellow-400 overflow-hidden" data-testid="aktionen-banner">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-black rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-black rounded-full translate-x-1/2 translate-y-1/2" />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center gap-6">
              {/* Navigation - Left */}
              {aktionen.length > 1 && (
                <button
                  onClick={prevAktion}
                  className="hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-black/10 hover:bg-black/20 transition-colors"
                  aria-label="Vorherige Aktion"
                >
                  <ChevronLeft className="w-6 h-6 text-black" />
                </button>
              )}

              {/* Aktion Content */}
              <div className="flex-1 flex items-center gap-6">
                {/* Image */}
                {aktionen[currentAktionIndex]?.bild_url && (
                  <div className="hidden sm:block w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden flex-shrink-0 shadow-lg">
                    <img 
                      src={aktionen[currentAktionIndex].bild_url.startsWith('http') 
                        ? aktionen[currentAktionIndex].bild_url 
                        : `${BACKEND_URL}${aktionen[currentAktionIndex].bild_url}`}
                      alt={aktionen[currentAktionIndex].titel}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4 text-black" />
                    <span className="text-xs font-bold text-black/70 uppercase tracking-wider">
                      Aktuelle Aktion
                    </span>
                    {aktionen.length > 1 && (
                      <span className="text-xs text-black/50">
                        {currentAktionIndex + 1} / {aktionen.length}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-black truncate">
                    {aktionen[currentAktionIndex]?.titel}
                  </h3>
                  <p className="text-sm text-black/70 line-clamp-1 md:line-clamp-2 mt-1">
                    {aktionen[currentAktionIndex]?.kurzbeschreibung || aktionen[currentAktionIndex]?.beschreibung}
                  </p>
                  {aktionen[currentAktionIndex]?.end_datum && (
                    <p className="text-xs text-black/60 mt-1">
                      Gültig bis {formatDateDE(aktionen[currentAktionIndex].end_datum)}
                    </p>
                  )}
                </div>

                {/* CTA */}
                <div className="flex-shrink-0">
                  <Link to="/aktionen">
                    <Button 
                      className="bg-black text-white hover:bg-black/80 font-bold"
                      data-testid="aktionen-banner-btn"
                    >
                      <span className="hidden sm:inline">Alle Aktionen</span>
                      <span className="sm:hidden">Mehr</span>
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Navigation - Right */}
              {aktionen.length > 1 && (
                <button
                  onClick={nextAktion}
                  className="hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-black/10 hover:bg-black/20 transition-colors"
                  aria-label="Nächste Aktion"
                >
                  <ChevronRight className="w-6 h-6 text-black" />
                </button>
              )}
            </div>

            {/* Dots indicator for mobile */}
            {aktionen.length > 1 && (
              <div className="flex justify-center gap-2 mt-4 md:hidden">
                {aktionen.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentAktionIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentAktionIndex ? 'bg-black' : 'bg-black/30'
                    }`}
                    aria-label={`Aktion ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )
    ),
    stats: (p) => (
<>
<section className="bg-black py-8" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {(parseStats(p.stats) || stats).map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-[#EFDF00]">
                  {stat.value}
                </p>
                <p className="text-[#D9D9D6] text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
</>
    ),
    services: (p) => (
<>
<section className="py-20 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black normal-case tracking-tight mb-4">
              {p.title ?? "Unsere Leistungen"}
            </h2>
            <div className="w-16 h-1 bg-[#EFDF00] mx-auto mb-6" />
            <p className="text-[#888B8D] max-w-2xl mx-auto">
              {p.subtitle ?? "Von der professionellen Wartung bis zum Fahrzeugkauf – wir bieten Ihnen alles aus einer Hand."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className="border border-[#D9D9D6] shadow-none hover:border-[#EFDF00] transition-colors"
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#EFDF00] rounded-none flex items-center justify-center text-black mb-6">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">
                    {service.title}
                  </h3>
                  <p className="text-[#888B8D] mb-6">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#888B8D]">
                        <CheckCircle className="w-4 h-4 text-[#EFDF00]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/termin">
              <Button 
                className="bg-black text-white hover:bg-[#333333] px-8 py-6"
                data-testid="services-termin-btn"
              >
                Jetzt Termin vereinbaren
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
</>
    ),
    vehicles: (p) => (
features.feature_fahrzeuge && (
      <section className="py-20 bg-[#F5F5F5]" data-testid="vehicles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black normal-case tracking-tight mb-4">
                {p.title ?? "Aktuelle Fahrzeuge"}
              </h2>
              <div className="w-16 h-1 bg-[#EFDF00] mb-4" />
              <p className="text-[#888B8D]">{p.subtitle ?? "Entdecken Sie unsere neuesten Angebote."}</p>
            </div>
            <Link to="/fahrzeuge" className="mt-4 md:mt-0">
              <Button variant="outline" className="border-black text-black hover:bg-black hover:text-white" data-testid="view-all-vehicles-btn">
                Alle Fahrzeuge
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#D9D9D6] h-96 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {vehicles.map((vehicle) => (
                <Card 
                  key={vehicle.id} 
                  className="overflow-hidden border border-[#D9D9D6] shadow-none hover:border-black transition-colors"
                  data-testid={`vehicle-card-${vehicle.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={vehicle.image_url} 
                      alt={`${vehicle.brand} ${vehicle.model}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      {vehicle.is_new ? (
                        <span className="bg-[#EFDF00] text-black px-3 py-1 text-xs font-bold">NEUWAGEN</span>
                      ) : (
                        <span className="bg-black text-white px-3 py-1 text-xs font-bold">GEBRAUCHTWAGEN</span>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-sm text-[#888B8D] font-medium">{vehicle.brand}</p>
                        <h3 className="text-xl font-bold text-black">
                          {vehicle.model}
                        </h3>
                      </div>
                      <p className="text-xl font-bold text-black">
                        {vehicle.price.toLocaleString('de-DE')} €
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-[#888B8D] mb-4">
                      <span>{vehicle.year}</span>
                      <span>•</span>
                      <span>{vehicle.mileage.toLocaleString('de-DE')} km</span>
                      <span>•</span>
                      <span>{vehicle.fuel_type}</span>
                      <span>•</span>
                      <span>{vehicle.transmission}</span>
                    </div>
                    <Link to="/kontakt">
                      <Button 
                        className="w-full bg-black text-white hover:bg-[#333333]"
                        data-testid={`vehicle-contact-${vehicle.id}`}
                      >
                        Anfragen
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
      )
    ),
    why_us: (p) => (
<>
<section className="py-20 bg-white" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black normal-case tracking-tight mb-6">
                {p.title ?? "Warum Auto Thurm?"}
              </h2>
              <div className="w-16 h-1 bg-[#EFDF00] mb-8" />
              <div className="space-y-6">
                {[
                  {
                    icon: <Award className="w-6 h-6" />,
                    title: "Zertifizierter Renault & Dacia Partner",
                    desc: "Offizieller Vertragshändler mit geschultem Personal und Originalteilen."
                  },
                  {
                    icon: <Users className="w-6 h-6" />,
                    title: "Familienbetrieb seit 25 Jahren",
                    desc: "Persönliche Beratung und langjährige Kundenbeziehungen."
                  },
                  {
                    icon: <Clock className="w-6 h-6" />,
                    title: "Schneller Service",
                    desc: "Kurze Wartezeiten und flexible Terminvergabe."
                  }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 bg-[#EFDF00] flex items-center justify-center text-black flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">{item.title}</h4>
                      <p className="text-[#888B8D] text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src={safeImage(p.image_url, BACKEND_URL) || "/img/aktion-fallback.jpg"}
                alt="Werkstatt"
                className="w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-black text-white p-6 hidden md:block">
                <p className="text-3xl font-bold text-[#EFDF00]">100%</p>
                <p className="text-sm">Kundenzufriedenheit</p>
              </div>
            </div>
          </div>
        </div>
      </section>
</>
    ),
    testimonials: (p) => (
features.feature_bewertungen && (
      <section className="py-20 bg-[#F5F5F5]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black normal-case tracking-tight mb-4">
              {p.title ?? "Das sagen unsere Kunden"}
            </h2>
            <div className="w-16 h-1 bg-[#EFDF00] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="border border-[#D9D9D6] shadow-none bg-white"
                data-testid={`testimonial-${index}`}
              >
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#EFDF00] text-[#EFDF00]" />
                    ))}
                  </div>
                  <p className="text-[#888B8D] mb-6 italic">"{testimonial.text}"</p>
                  <p className="font-bold text-black">{testimonial.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      )
    ),
    cta: (p) => (
features.feature_termine && (
      <section className="py-20 bg-black" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white normal-case tracking-tight mb-6">
            {p.title ?? "Bereit für Ihren nächsten Besuch?"}
          </h2>
          <p className="text-[#D9D9D6] max-w-2xl mx-auto mb-8">
            {p.text ?? "Vereinbaren Sie jetzt einen Termin oder rufen Sie uns direkt an. Wir freuen uns auf Sie!"}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/termin">
              <Button 
                className="bg-[#EFDF00] text-black hover:bg-[#D9C900] font-bold px-8 py-6 text-lg"
                data-testid="cta-termin-btn"
              >
                Termin buchen
              </Button>
            </Link>
            <a href={telHref(site.company_phone)}>
              <Button 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg bg-transparent"
                data-testid="cta-phone-btn"
              >
                <Phone className="mr-2 w-5 h-5" />
                {site.company_phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
      )
    ),
    callback: (p) => (
!features.feature_kontakt && (
        <section className="py-20 bg-[#F2F2F2]" data-testid="home-callback-section">
          <div className="max-w-xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-black mb-3">{p.title ?? "Rückruf anfordern"}</h2>
            <p className="text-[#888B8D] mb-8">{p.text ?? "Hinterlassen Sie Ihren Namen und Ihre Telefonnummer oder E-Mail - wir melden uns."}</p>
            <CallbackForm />
          </div>
        </section>
      )
    ),
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {(layout || []).filter((b) => b.visible !== false).map((b) => (
        <BlockFrame key={b.id} block={b} preview={preview} selected={selectedId === b.id} label={BLOCK_SCHEMAS[b.type]?.label || b.type}>
          {builtins[b.type] ? builtins[b.type](b.props || {}) : <CustomBlock block={b} />}
        </BlockFrame>
      ))}
      <Footer />
    </div>
  );
};

export default HomePage;
